'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { trackUserDataEvent } from '@/integrations/tracking/google-tag-manager/scripts';

type UserProfile = Content.Components['GetUserProfileResponse'];
type Companion = Content.Components['TravelCompanionDto'];

const getCompanionsFromUserProfile = (
  userProfile?: UserProfile,
): Companion[] => {
  return userProfile?.userProfile.companions ?? [];
};

export interface UserProfileContextState {
  companions?: Companion[];
  isUserConsentsUpdated?: boolean;
  isUserInformationUpdated?: boolean;
  profileSubPageId: string | null;
  setCompanions: (value: Companion[]) => void;
  setIsUserConsentsUpdated: (value: boolean) => void;
  setIsUserInformationUpdated: (value: boolean) => void;
  setProfileSubPageId: (id: string | null) => void;
  setUserProfile?: (value: UserProfile) => void;
  userProfile?: UserProfile;
}

const UserProfileContext = createContext<UserProfileContextState | undefined>(
  undefined,
);

export const UserProfileContextProvider = ({
  children,
  initialData,
}: {
  children: ReactNode;
  initialData?: {
    isUserConsentsUpdated?: boolean;
    isUserInformationUpdated?: boolean;
    subPageId?: string;
    userProfile?: UserProfile;
  };
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const querySubPageId = searchParams?.get('profileSubPageId');

  const [profileSubPageId, setProfileSubPageId] = useState<string | null>(
    () => {
      return querySubPageId ?? initialData?.subPageId ?? null;
    },
  );
  const [isUserConsentsUpdated, setIsUserConsentsUpdated] = useState(
    initialData?.isUserConsentsUpdated ?? false,
  );
  const [companions, setCompanions] = useState<Companion[] | undefined>(
    initialData?.userProfile?.userProfile.companions ??
      getCompanionsFromUserProfile(initialData?.userProfile),
  );
  const [isUserInformationUpdated, setIsUserInformationUpdated] = useState(
    initialData?.isUserInformationUpdated ?? false,
  );
  const [userProfile, setUserProfile] = useState<UserProfile | undefined>(
    initialData?.userProfile,
  );
  const lastTrackedUserDataRef = useRef<string | null>(null);

  useEffect(() => {
    const hydratedCompanions = getCompanionsFromUserProfile(userProfile);

    if (!hydratedCompanions) return;

    setCompanions(hydratedCompanions);
  }, [userProfile]);

  // Redirect to given url (onboarding flow) if user has missing mandatory consents
  useEffect(() => {
    if (!userProfile?.hasMissingConsents) return;

    const missingConsentUrl = userProfile.missingConsentLink?.url;

    if (!missingConsentUrl || typeof window === 'undefined') return;

    if (window.location.pathname === missingConsentUrl) return;

    const returnTo = `${window.location.pathname}${window.location.search}`;

    const redirectUrl =
      returnTo === '/'
        ? missingConsentUrl
        : `${missingConsentUrl}?returnTo=${encodeURIComponent(returnTo)}`;

    router.replace(redirectUrl);
  }, [router, userProfile]);

  useEffect(() => {
    const userDetails = userProfile?.userProfile.details;

    if (!userDetails) return;

    const userData = {
      email: userDetails.email ?? undefined,
      first_name: userDetails.firstName ?? undefined,
      last_name: userDetails.lastName ?? undefined,
    };

    if (!userData.email && !userData.first_name && !userData.last_name) {
      return;
    }

    const signature = JSON.stringify(userData);

    if (lastTrackedUserDataRef.current === signature) {
      return;
    }

    lastTrackedUserDataRef.current = signature;

    trackUserDataEvent({ user_data: userData });
  }, [userProfile]);

  return (
    <UserProfileContext.Provider
      value={{
        companions,
        isUserConsentsUpdated,
        isUserInformationUpdated,
        profileSubPageId,
        setCompanions,
        setIsUserConsentsUpdated,
        setIsUserInformationUpdated,
        setProfileSubPageId,
        setUserProfile,
        userProfile,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfileContext = () => {
  const context = useContext(UserProfileContext);

  if (!context) {
    throw new Error(
      'useUserProfileContext must be used within a UserProfileContextProvider',
    );
  }

  return context;
};
